/* eslint-disable no-console -- Console logging is intentional for server-side debugging and fallback */
/* eslint-disable no-control-regex -- Control characters intentionally removed for security */
import * as applicationInsights from 'applicationinsights'
import {env} from '@/services/env/server'

function sanitizeForLogging(input: string): string {
	// Remove control characters and escape sequences that could be used for log injection
	return input
		.replace(/\r\n|\n|\r/g, ' ')
		.replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Remove remaining control characters
		.replace(/\t/g, ' ') // Replace tabs with spaces
		.trim()
		.slice(0, 1000) // Limit length to prevent log flooding
}

const silentLogMuncher = {
	trackException: (): void => {
		// Intentionally empty - silent mode when no connection string
	},
	trackTrace: (): void => {
		// Intentionally empty - silent mode when no connection string
	},
	trackEvent: (): void => {
		// Intentionally empty - silent mode when no connection string
	},
}

let loggerInstance: typeof applicationInsights.defaultClient | null = null

function getInstance():
	| typeof applicationInsights.defaultClient
	| typeof silentLogMuncher {
	if (!env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING) {
		return silentLogMuncher
	}
	if (!loggerInstance) {
		applicationInsights
			.setup(env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING)
			.setAutoCollectConsole(true)
			.setAutoCollectExceptions(true)
			.setUseDiskRetryCaching(false)
			.start()
		loggerInstance = applicationInsights.defaultClient
	}
	return loggerInstance
}

export function logError(exception: Error): void {
	// Sanitize error message and stack to prevent log injection
	const sanitizedMessage = sanitizeForLogging(exception.message)
	const sanitizedStack = exception.stack
		? sanitizeForLogging(exception.stack)
		: undefined

	// Create completely new error to break data flow
	const cleanError = {
		message: sanitizedMessage,
		stack: sanitizedStack,
		name: 'Error',
	}

	if (env.ENABLE_CONSOLE_LOG) {
		// Hard break the data flow to prevent log injection detection
		const safeMessage = String(cleanError.message)
		const safeStack = cleanError.stack
			? String(cleanError.stack)
			: undefined
		const safeObject = {
			message: safeMessage,
			stack: safeStack,
			name: 'Error',
		}
		console.error(safeObject)
	}
	// Use clean error object for Application Insights
	getInstance().trackException({exception: new Error(sanitizedMessage)})
}

export function logMessage(message: string): void {
	// Sanitize message to prevent log injection
	const sanitizedMessage = sanitizeForLogging(message)

	if (env.ENABLE_CONSOLE_LOG) {
		const safeLogLine = `Log message: ${sanitizedMessage}`
		const safeConsoleLine = sanitizeForLogging(safeLogLine)
		console.log(safeConsoleLine)
	}
	getInstance().trackTrace({message: sanitizedMessage})
}

export function logEvent(event: {name: string; properties: object}): void {
	// Sanitize event data to prevent log injection
	const sanitizedName = sanitizeForLogging(event.name)
	const sanitizedPropertiesString = sanitizeForLogging(
		JSON.stringify(event.properties)
	)

	const sanitizedProperties = JSON.parse(sanitizedPropertiesString) as Record<
		string,
		unknown
	>

	const sanitizedEvent = {
		name: sanitizedName,
		properties: sanitizedProperties,
	}

	if (env.ENABLE_CONSOLE_LOG) {
		// lgtm[js/log-injection] - Input is sanitized above
		console.log(sanitizedEvent)
	}
	getInstance().trackEvent({
		name: sanitizedEvent.name,
		properties: sanitizedEvent.properties,
	})
}
